import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DROPDOWN_SEARCH_DEFAULT_MAX_LENGTH } from '~/components/DropdownTreeSingleSelect/constants';
import { Icon } from '~/components/Icon';
import { IconButton } from '~/components/IconButton';
import { cn } from '~/lib/cn';

interface DropdownSearchProps {
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
}

export function DropdownSearch({
  value,
  onChange,
  maxLength = DROPDOWN_SEARCH_DEFAULT_MAX_LENGTH,
}: DropdownSearchProps) {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => {
      window.clearTimeout(timeoutRef.current);
    };
  }, []);

  /**
   * Prevent "@radix-ui/react-select" from hijacking keystrokes for its navigation.
   * For instance, without this, typing the letter "a" would focus the cursor on
   * the first dropdown option starting with the letter "a".
   */
  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();

      if (!['Tab'].includes(event.key)) {
        timeoutRef.current = window.setTimeout(() => {
          inputRef.current?.focus();
        }, 10);
      }
    },
    [],
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation();
      onChange(event.target.value);
    },
    [onChange],
  );

  const handleClearSearchButtonClick = useCallback(() => {
    onChange('');

    timeoutRef.current = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 10);
  }, [onChange]);

  return (
    <div className="relative flex items-center justify-between border-b-px p-md border-b-emphasis-default">
      <Icon
        className="pointer-events-none absolute pl-[1em] text-subtle"
        type="magnifying-glass"
        variant="solid"
        size="sm"
        aria-hidden
      />
      <input
        ref={inputRef}
        type="text"
        value={value}
        maxLength={maxLength}
        className={cn(
          'grow rounded-xs px-xl py-sm text-body-sm duration-300 ease-out text-default placeholder:text-subtle',
          'border outline-none bg-emphasis-default border-emphasis-subtle hover:bg-emphasis-strong hover:border-emphasis-strong',
        )}
        placeholder={t('dropdownTree.searchPlaceholder')}
        onKeyDown={handleInputKeyDown}
        onChange={handleInputChange}
        aria-label={t('dropdownTree.searchInputLabel')}
        role="searchbox"
      />
      {value ? (
        <IconButton
          type="button"
          iconType="circle-xmark"
          size="small"
          className="absolute right-[1.5em] duration-300 text-subtle hover:text-icon-strong"
          onClick={handleClearSearchButtonClick}
          aria-label={t('search.clearInput')}
        />
      ) : null}
    </div>
  );
}

DropdownSearch.displayName = 'DropdownSearch';
